import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from "firebase/auth";
import { auth } from '../../firebase';
import Header from './Header';
import Sidebar from './Sidebar';
import ContentArea from './ContentArea';
import UploadSection from './UploadSection';
import { fetchFAQs, fetchUtterances, fetchAnswers } from '../../utils/dataFetchers';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState('faq'); // Changed from 'utterance' to 'faq'
  const [showForm, setShowForm] = useState(false);
  const [faqs, setFaqs] = useState({});
  const [utterances, setUtterances] = useState({});
  const [answers, setAnswers] = useState({});
  const [showUploadInputs, setShowUploadInputs] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
        Promise.all([
          fetchFAQs(),
          fetchUtterances(),
          fetchAnswers()
        ]).then(([fetchedFaqs, fetchedUtterances, fetchedAnswers]) => {
          setFaqs(fetchedFaqs);
          setUtterances(fetchedUtterances);
          setAnswers(fetchedAnswers);
          setLoading(false);
        }).catch(error => {
          console.error("Error fetching data:", error);
          setLoading(false);
        });
      } else {
        navigate('/login');
      }
    });
  
    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setShowForm(false);
  };

  return (
    <div className="flex flex-col h-screen">
      <Header user={user} onLogout={handleLogout} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          showUploadInputs={showUploadInputs}
          setShowUploadInputs={setShowUploadInputs}
        />
        {loading ? (
          <div className="w-3/4 flex items-center justify-center">
            <p>Loading...</p>
          </div>
        ) : (
          <ContentArea
            activeSection={activeSection}
            showForm={showForm}
            setShowForm={setShowForm}
            faqs={faqs}
            utterances={utterances}
            answers={answers}
            user={user}
            onDataUpdate={() => {
              setLoading(true);
              Promise.all([
                fetchFAQs(),
                fetchUtterances(),
                fetchAnswers()
              ]).then(([fetchedFaqs, fetchedUtterances, fetchedAnswers]) => {
                setFaqs(fetchedFaqs);
                setUtterances(fetchedUtterances);
                setAnswers(fetchedAnswers);
                setLoading(false);
              }).catch(error => {
                console.error("Error updating data:", error);
                setLoading(false);
              });
            }}
          />
        )}
      </div>
      {showUploadInputs && (
        <UploadSection
          onUploadSuccess={() => {
            setLoading(true);
            Promise.all([
              fetchFAQs(),
              fetchUtterances(),
              fetchAnswers()
            ]).then(([fetchedFaqs, fetchedUtterances, fetchedAnswers]) => {
              setFaqs(fetchedFaqs);
              setUtterances(fetchedUtterances);
              setAnswers(fetchedAnswers);
              setLoading(false);
            }).catch(error => {
              console.error("Error updating data after upload:", error);
              setLoading(false);
            });
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;